import React, { useRef, useEffect, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
// 修改导入方式
import 'leaflet-curve';
// 添加热力图插件导入
import 'leaflet.heat';
import './index.css';
import { tenantUserNetwork } from '@/services/athena';
import { useLocation } from 'umi';
// 修复 Leaflet 默认图标问题
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});
const HotChart = (props) => {
  // eslint-disable-next-line react/prop-types
  const { coachUserId } = props;
  const location = useLocation();
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const curvesRef = useRef([]);
  const heatLayerRef = useRef(null); // 添加热力图层引用
  const rangeCircleRef = useRef(null); // 添加范围圆圈引用
  const [coachData, setCoachData] = useState({});
  const [showHeatmap, setShowHeatmap] = useState(false); // 添加热力图显示状态
  const [showDistanceFilter, setShowDistanceFilter] = useState(false); // 添加距离过滤状态
  const [filterDistance, setFilterDistance] = useState(10); // 添加距离输入状态，默认10km
  const [showFlowLines, setShowFlowLines] = useState(true); // 添加流向线显示状态
  const [showRightsFilter, setShowRightsFilter] = useState(false); // 添加权益筛选状态，默认关闭
  const [rfmFilter, setRfmFilter] = useState('all'); // 添加RFM筛选状态，默认显示全部
  const [userStats, setUserStats] = useState({ total: 0, filtered: 0 }); // 添加用户统计
  // 定义RFM用户类型选项
  const rfmOptions = [
    { value: 'all', label: '全部用户' },
    { value: '重要价值用户', label: '重要价值用户' },
    { value: '重要发展用户', label: '重要发展用户' },
    { value: '重要保持用户', label: '重要保持用户' },
    { value: '重要挽留用户', label: '重要挽留用户' },
    { value: '一般价值用户', label: '一般价值用户' },
    { value: '一般发展用户', label: '一般发展用户' },
    { value: '一般保持用户', label: '一般保持用户' },
    { value: '潜在用户', label: '潜在用户' },
  ];

  // 处理RFM筛选变化
  const handleRfmFilterChange = (e) => {
    setRfmFilter(e.target.value);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await tenantUserNetwork({
          requestData: {
            coachUserId: coachUserId || location?.query?.coachUserId,
          },
        });
        console.log('res', res);
        setCoachData(res?.data || { memberGeoModels: [] });
      } catch (error) {
        console.log('error', error);
      }
    };
    fetchData();
  }, [coachUserId, location?.query?.coachUserId]);
  // 添加距离计算函数（使用Haversine公式）
  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371; // 地球半径（公里）
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // 返回距离（公里）
  };

  // 添加过滤指定范围内用户的函数
  const filterUsersWithinRange = (
    users,
    coachLat,
    coachLng,
    rangeKm = filterDistance,
  ) => {
    if (!coachLat || !coachLng || !users) return users;

    return users.filter((user) => {
      if (!user?.latitude || !user?.longitude) return false;

      const userLat = parseFloat(user.latitude);
      const userLng = parseFloat(user.longitude);
      const distance = calculateDistance(coachLat, coachLng, userLat, userLng);

      return distance <= rangeKm;
    });
  };

  // 添加或移除范围圆圈
  const toggleRangeCircle = (show, distance = filterDistance) => {
    if (!mapRef.current) return;

    // 移除现有圆圈
    if (rangeCircleRef.current) {
      mapRef.current.removeLayer(rangeCircleRef.current);
      rangeCircleRef.current = null;
    }

    if (show && coachData?.coachLatitude && coachData?.coachLongitude) {
      const coachLat = parseFloat(coachData.coachLatitude);
      const coachLng = parseFloat(coachData.coachLongitude);

      // 创建指定范围圆圈
      rangeCircleRef.current = L.circle([coachLat, coachLng], {
        color: '#ff6b6b',
        fillColor: '#ff6b6b',
        fillOpacity: 0.1,
        radius: distance * 1000, // km转换为m
        weight: 2,
        opacity: 0.8,
        dashArray: '5, 5',
      }).addTo(mapRef.current);

      // 添加提示标签
      rangeCircleRef.current.bindTooltip(`${distance}km范围`, {
        permanent: false,
        direction: 'center',
        className: 'range-tooltip',
      });
    }
  };

  // 切换距离过滤状态
  const toggleDistanceFilter = () => {
    setShowDistanceFilter((prev) => {
      const newState = !prev;
      toggleRangeCircle(newState);
      return newState;
    });
  };

  // 切换权益筛选状态
  const toggleRightsFilter = () => {
    setShowRightsFilter((prev) => !prev);
  };

  // 处理距离输入变化
  const handleDistanceChange = (e) => {
    const value = parseFloat(e.target.value);

    setFilterDistance(value);

    // 如果距离过滤开启，立即更新所有相关元素
    if (
      showDistanceFilter &&
      coachData?.coachLatitude &&
      coachData?.coachLongitude
    ) {
      // 更新范围圆圈
      toggleRangeCircle(true, value);

      // 重新添加城市标记以应用新的距离过滤
      addCityMarkers();

      // 如果热力图开启，重新生成热力图
      if (showHeatmap) {
        addHeatmapLayer();
      }

      // 如果流向线开启，重新生成流向线
      if (showFlowLines) {
        addFlowLines();
      }
    }
  };

  // 获取当前应显示的用户数据
  const getCurrentUserData = () => {
    if (!coachData?.memberGeoModels) return [];

    let filteredUsers = coachData.memberGeoModels;

    // 权益筛选
    if (showRightsFilter) {
      filteredUsers = filteredUsers.filter((user) => user?.hasRights === true);
    }

    // RFM用户分群筛选
    if (rfmFilter !== 'all') {
      filteredUsers = filteredUsers.filter(
        (user) => user?.rfmType === rfmFilter,
      );
    }

    // 距离筛选
    if (showDistanceFilter) {
      const coachLat = parseFloat(coachData.coachLatitude);
      const coachLng = parseFloat(coachData.coachLongitude);

      // 如果教练坐标为空，距离过滤无效，返回权益筛选后的用户
      if (!coachLat || !coachLng) {
        return filteredUsers;
      }

      filteredUsers = filterUsersWithinRange(
        filteredUsers,
        coachLat,
        coachLng,
        filterDistance,
      );
    }

    return filteredUsers;
  };

  // 修改生成流向数据函数，支持过滤
  const generateFlowData = () => {
    const flowData = [];
    const flows = [];

    // 如果教练坐标为空，不生成流向线
    if (!coachData?.coachLatitude || !coachData?.coachLongitude) {
      return flowData;
    }

    const coachLat = parseFloat(coachData.coachLatitude);
    const coachLng = parseFloat(coachData.coachLongitude);
    const coachCoords = [coachLat, coachLng];

    // 使用过滤后的用户数据
    const currentUsers = getCurrentUserData();

    currentUsers?.forEach((member) => {
      if (!member?.latitude || !member?.longitude) {
        return;
      }

      const originalLat = parseFloat(member.latitude);
      const originalLng = parseFloat(member.longitude);

      // 使用转换后的坐标绘制流向线
      const transformed = transformCoordinates(
        originalLat,
        originalLng,
        coachLat,
        coachLng,
      );
      const memberCoords = [transformed.lat, transformed.lng];

      // 检查坐标是否重合
      const isOverlapping =
        coachCoords[0] === memberCoords[0] &&
        coachCoords[1] === memberCoords[1];

      if (!isOverlapping) {
        flows.push({
          fromName: coachData?.coachNickName,
          toName: member?.nickName,
          fromCoords: coachCoords,
          toCoords: memberCoords,
          value: Math.floor(Math.random() * 100) + 50,
        });
      }
    });

    flowData.push(flows);
    return flowData;
  };

  // 创建自定义图标
  const createCustomIcon = (color, isCoach = false) => {
    const visualSize = isCoach ? 20 : 15; // 视觉大小保持不变
    const clickSize = isCoach ? 40 : 30; // 点击区域放大
    return L.divIcon({
      className: 'custom-marker',
      html: `<div style="
        width: ${visualSize}px;
        height: ${visualSize}px;
        background-color: ${color};
        border: 2px solid white;
        border-radius: 50%;
        box-shadow: 0 0 10px rgba(${
          color === '#ff6b6b' ? '255,107,107' : '70,189,228'
        }, 0.8);
        animation: pulse 2s infinite;
        margin: ${(clickSize - visualSize) / 2}px;
      "></div>`,
      iconSize: [clickSize, clickSize], // 扩大点击区域
      iconAnchor: [clickSize / 2, clickSize / 2], // 调整锚点
    });
  };

  // 移除流向线
  const removeFlowLines = () => {
    curvesRef.current.forEach((curve) => {
      if (mapRef.current && mapRef.current.hasLayer(curve)) {
        mapRef.current.removeLayer(curve);
      }
    });
    curvesRef.current = [];
  };

  // 切换流向线显示
  const toggleFlowLines = () => {
    const newShowFlowLines = !showFlowLines;
    setShowFlowLines(newShowFlowLines);

    if (!newShowFlowLines) {
      // 如果要关闭流向线，立即移除
      removeFlowLines();
    } else {
      // 如果要开启流向线，立即添加
      const flowData = generateFlowData();
      const colors = ['#4ecdc4', '#ffa022', '#a6c84c', '#ff6b6b', '#4ecdc4'];

      // 清除现有的曲线
      curvesRef.current.forEach((curve) => {
        if (mapRef.current && mapRef.current.hasLayer(curve)) {
          mapRef.current.removeLayer(curve);
        }
      });
      curvesRef.current = [];

      flowData.forEach((flows, index) => {
        const color = colors[index % colors.length];

        flows.forEach((flow) => {
          // 使用 leaflet-curve 创建曲线
          const curve = L.curve(
            [
              'M',
              flow.fromCoords,
              'Q',
              [
                (flow.fromCoords[0] + flow.toCoords[0]) / 2 + 5, // 控制点纬度偏移
                (flow.fromCoords[1] + flow.toCoords[1]) / 2,
              ],
              flow.toCoords,
            ],
            {
              color: color,
              weight: 2,
              opacity: 0.7,
              animate: {
                duration: 3000,
                iterations: Infinity,
              },
            },
          );

          // 添加点击事件
          curve.on('click', () => {
            handleFlowClick(flow);
          });

          // 添加悬停效果
          curve.on('mouseover', function () {
            // this.setStyle({ weight: 4, opacity: 1 });
          });

          curve.on('mouseout', function () {
            // this.setStyle({ weight: 2, opacity: 0.7 });
          });

          curve.addTo(mapRef.current);
          curvesRef.current.push(curve);

          // 添加流向动画点
          const animatedMarker = L.circleMarker(flow.fromCoords, {
            radius: 3,
            fillColor: color,
            color: 'white',
            weight: 1,
            opacity: 1,
            fillOpacity: 0.8,
          }).addTo(mapRef.current);

          curvesRef.current.push(animatedMarker);

          // 动画移动标记
          let progress = 0;
          const animate = () => {
            progress += 0.01;
            if (progress > 1) progress = 0;

            const point = curve.getPathString
              ? curve.getLatLng
                ? curve.getLatLng()
                : flow.fromCoords
              : [
                  flow.fromCoords[0] +
                    (flow.toCoords[0] - flow.fromCoords[0]) * progress,
                  flow.fromCoords[1] +
                    (flow.toCoords[1] - flow.fromCoords[1]) * progress,
                ];

            animatedMarker.setLatLng(point);
            requestAnimationFrame(animate);
          };
          animate();
        });
      });
    }
  };

  // 添加流向线
  const addFlowLines = () => {
    const flowData = generateFlowData();
    const colors = ['#4ecdc4', '#ffa022', '#a6c84c', '#ff6b6b', '#4ecdc4'];

    // 清除现有的曲线
    curvesRef.current.forEach((curve) => {
      if (mapRef.current && mapRef.current.hasLayer(curve)) {
        mapRef.current.removeLayer(curve);
      }
    });
    curvesRef.current = [];

    flowData.forEach((flows, index) => {
      const color = colors[index % colors.length];

      flows.forEach((flow) => {
        // 使用 leaflet-curve 创建曲线
        const curve = L.curve(
          [
            'M',
            flow.fromCoords,
            'Q',
            [
              (flow.fromCoords[0] + flow.toCoords[0]) / 2 + 5, // 控制点纬度偏移
              (flow.fromCoords[1] + flow.toCoords[1]) / 2,
            ],
            flow.toCoords,
          ],
          {
            color: color,
            weight: 2,
            opacity: 0.7,
            animate: {
              duration: 3000,
              iterations: Infinity,
            },
          },
        );

        // 添加点击事件
        curve.on('click', () => {
          handleFlowClick(flow);
        });

        // 添加悬停效果
        curve.on('mouseover', function () {
          // this.setStyle({ weight: 4, opacity: 1 });
        });

        curve.on('mouseout', function () {
          // this.setStyle({ weight: 2, opacity: 0.7 });
        });

        curve.addTo(mapRef.current);
        curvesRef.current.push(curve);

        // 添加流向动画点
        const animatedMarker = L.circleMarker(flow.fromCoords, {
          radius: 3,
          fillColor: color,
          color: 'white',
          weight: 1,
          opacity: 1,
          fillOpacity: 0.8,
        });

        animatedMarker.addTo(mapRef.current);
        curvesRef.current.push(animatedMarker);

        // 动画移动点沿曲线移动
        let progress = 0;
        const animateMarker = () => {
          progress += 0.01;
          if (progress > 1) progress = 0;

          // 简化的贝塞尔曲线计算
          const t = progress;
          const lat =
            Math.pow(1 - t, 2) * flow.fromCoords[0] +
            2 *
              (1 - t) *
              t *
              ((flow.fromCoords[0] + flow.toCoords[0]) / 2 + 5) +
            Math.pow(t, 2) * flow.toCoords[0];
          const lng =
            Math.pow(1 - t, 2) * flow.fromCoords[1] +
            (2 * (1 - t) * t * (flow.fromCoords[1] + flow.toCoords[1])) / 2 +
            Math.pow(t, 2) * flow.toCoords[1];

          animatedMarker.setLatLng([lat, lng]);

          if (mapRef.current) {
            requestAnimationFrame(animateMarker);
          }
        };

        animateMarker();
      });
    });
  };
  // 坐标转换函数 - 以教练为中心重新计算用户位置
  const transformCoordinates = (userLat, userLng, coachLat, coachLng) => {
    if (!userLat || !userLng || !coachLat || !coachLng) {
      return { lat: userLat, lng: userLng };
    }
    // 计算相对于教练的经度差
    let lngDiff = userLng - coachLng;

    // 处理跨越180度经线的情况
    if (lngDiff > 180) {
      lngDiff -= 360;
    } else if (lngDiff < -180) {
      lngDiff += 360;
    }

    // 返回转换后的坐标
    return {
      lat: userLat,
      lng: coachLng + lngDiff,
    };
  };
  // 修改添加城市标记函数，支持过滤
  const addCityMarkers = () => {
    // 清除现有标记
    markersRef.current.forEach((marker) => {
      if (mapRef.current && mapRef.current.hasLayer(marker)) {
        mapRef.current.removeLayer(marker);
      }
    });
    markersRef.current = [];

    // 获取教练坐标
    const coachLat = coachData?.coachLatitude
      ? parseFloat(coachData.coachLatitude)
      : null;
    const coachLng = coachData?.coachLongitude
      ? parseFloat(coachData.coachLongitude)
      : null;

    // 使用过滤后的用户数据
    const currentUsers = getCurrentUserData();

    // 更新用户统计
    setUserStats({
      total: coachData?.memberGeoModels?.length || 0,
      filtered: currentUsers.length,
    });

    // 添加用户标记
    currentUsers?.forEach((member) => {
      if (!member?.latitude || !member?.longitude) {
        return;
      }
      const originalLat = parseFloat(member.latitude);
      const originalLng = parseFloat(member.longitude);

      // 如果教练坐标存在，使用转换后的坐标；否则使用原始坐标
      let coords;
      if (coachLat && coachLng) {
        const transformed = transformCoordinates(
          originalLat,
          originalLng,
          coachLat,
          coachLng,
        );
        coords = [transformed.lat, transformed.lng];
      } else {
        coords = [originalLat, originalLng];
      }

      const marker = L.marker(coords, {
        icon: createCustomIcon('#46bee9'),
      });

      marker.bindPopup(
        `<b>${member?.nickName}</b><br/>用户位置<br/>城市: ${member?.city}<br/>电话: ${member?.phone}<br/>原始坐标: ${originalLat}, ${originalLng}`,
      );
      marker.on('click', () => {
        handleCityClick({
          name: member?.nickName,
          coords,
          originalCoords: [originalLat, originalLng],
          type: 'user',
          data: member,
        });
      });

      try {
        marker.addTo(mapRef.current);
        markersRef.current.push(marker);
      } catch (e) {
        console.log('添加用户标记失败', e);
      }
    });

    // 添加教练标记（如果教练坐标存在）
    if (coachLat && coachLng) {
      const coachCoords = [coachLat, coachLng];
      const coachMarker = L.marker(coachCoords, {
        icon: createCustomIcon('#ff6b6b', true),
      });

      coachMarker.bindPopup(
        `<b>${coachData?.coachNickName}</b><br/>教练位置<br/>电话: ${coachData?.coachPhone}`,
      );
      coachMarker.on('click', () => {
        handleCityClick({
          name: coachData?.coachNickName,
          coords: coachCoords,
          type: 'coach',
          data: coachData,
        });
      });

      try {
        coachMarker.addTo(mapRef.current);
        markersRef.current.push(coachMarker);
      } catch (e) {
        console.log('添加教练标记失败', e);
      }
    }
  };

  // 检测重合坐标的函数
  const findOverlappingUsers = (clickedCoords) => {
    const overlapping = [];

    // 获取教练的原始坐标，用于转换用户坐标
    const coachLat = coachData?.coachLatitude
      ? parseFloat(coachData.coachLatitude)
      : null;
    const coachLng = coachData?.coachLongitude
      ? parseFloat(coachData.coachLongitude)
      : null;

    // 使用过滤后的用户数据进行重合检测
    const currentUsers = getCurrentUserData();

    // 先检查所有用户坐标 - 使用转换后的坐标进行比较
    currentUsers?.forEach((member) => {
      if (!member?.latitude || !member?.longitude || !coachLat || !coachLng) {
        return;
      }
      const originalUserLat = parseFloat(member.latitude);
      const originalUserLng = parseFloat(member.longitude);

      // 对用户坐标进行转换
      const transformedUser = transformCoordinates(
        originalUserLat,
        originalUserLng,
        coachLat,
        coachLng,
      );
      const userMapCoords = [transformedUser.lat, transformedUser.lng];

      // 使用地图上显示的坐标进行比较
      if (
        userMapCoords[0] === clickedCoords[0] &&
        userMapCoords[1] === clickedCoords[1]
      ) {
        overlapping.push({
          name: member?.nickName,
          coords: userMapCoords, // 保存地图上实际显示的坐标
          originalCoords: [originalUserLat, originalUserLng], // 也可选择保存原始坐标
          type: 'user',
          data: member,
        });
      }
    });

    // 后检查教练坐标，教练坐标在地图上是原始坐标
    if (coachLat && coachLng) {
      const coachMapCoords = [coachLat, coachLng];
      if (
        coachMapCoords[0] === clickedCoords[0] &&
        coachMapCoords[1] === clickedCoords[1]
      ) {
        overlapping.unshift({
          name: coachData?.coachNickName,
          coords: coachMapCoords,
          type: 'coach',
          data: coachData,
        });
      }
    }
    return overlapping;
  };

  // 创建重合标记的弹窗内容
  const createOverlapPopupContent = (overlappingItems) => {
    if (overlappingItems.length <= 1) {
      const item = overlappingItems[0];
      if (item?.type === 'coach') {
        return `
          <div class="user-detail-popup">
            <h3>${item?.name}</h3>
            <p><strong>类型:</strong> 教练</p>
            <p><strong>电话:</strong> ${item?.data?.coachPhone}</p>
            <p><strong>IP:</strong> ${item?.data?.ip}</p>
          </div>
        `;
      } else {
        return `
          <div class="user-detail-popup">
            <h3>${item?.name}</h3>
            <p><strong>类型:</strong> 用户</p>
            <p><strong>城市:</strong> ${item?.data?.city}</p>
            <p><strong>IP:</strong> ${item?.data?.ip}</p>
            <p><strong>电话:</strong> ${item?.data?.phone}</p>
            <p><strong>总完课数:</strong> ${getNullString(
              item?.data?.finishClassCount,
            )}次</p>
            <p><strong>总训练分钟数:</strong> ${getNullString(
              item?.data?.totalMinutes,
            )}分钟</p>
            <p><strong>最近30天运动打卡次数:</strong> ${getNullString(
              item?.data?.finishCountLatest30,
            )}次</p>
            <p><strong>最近3个月消费金额:</strong> ${getNullString(
              item?.data?.orderCostLatest90,
            )}元</p>
            <p><strong>最近活跃间隔:</strong> ${getNullString(
              item?.data?.recencyInteractionDays,
            )}天</p>
          </div>
        `;
      }
    }

    // 多个重合项的处理
    let content = `
      <div class="overlap-popup">
        <h3 style="margin: 0 0 10px 0; padding: 0; font-size: 16px;">此处有多个标记</h3>
        <ul style="
          list-style: none;
          padding: 0;
          margin: 0;
          max-height: 300px;
          overflow-y: auto;
          border: 1px solid #e0e0e0;
          border-radius: 4px;
        ">
    `;

    overlappingItems?.forEach((item, index) => {
      console.log('item', item);
      const typeText = item?.type === 'coach' ? '教练' : '用户';
      const extraInfo =
        item?.type === 'coach'
          ? `电话: ${item?.data?.coachPhone}`
          : `电话: ${item?.data?.phone}`;
      content += `
        <li class="overlap-item"
            onclick="window.handleOverlapItemClick('${item?.name}', '${
        item?.type
      }', [${item?.coords?.[0]}, ${item?.coords?.[1]}], '${
        item?.type === 'coach' ? item?.data?.coachPhone : item?.data?.phone
      }', '${
        item?.type === 'coach' ? item?.data?.coachUserId : item?.data?.userId
      }', '${item?.data?.city}', '${item?.data?.ip}', '${JSON.stringify(item)
        .replace(/'/g, '&#39;')
        .replace(/"/g, '&quot;')}')"
            style="
              cursor: pointer;
              padding: 12px;
              margin: 0;
              border-bottom: 1px solid #f0f0f0;
              background: #fafafa;
              transition: background 0.2s;
              display: block;
            "
            onmouseover="this.style.background='#e9e9e9'"
            onmouseout="this.style.background='#fafafa'">
          <strong style="color: ${
            item?.type === 'coach' ? '#ff6b6b' : '#46bee9'
          }; font-size: 14px;">${item?.name}</strong>
          <span style="color: #666; font-size: 12px;">(${typeText})</span><br/>
          <small style="color: #888; font-size: 11px;">${extraInfo}</small>
        </li>
      `;
    });

    content += `
        </ul>
        <div style="margin-top: 8px; font-size: 11px; color: #999; text-align: center;">
          共 ${overlappingItems?.length} 个标记
        </div>
      </div>
    `;

    return content;
  };

  function getNullString(str) {
    if (
      str === '' ||
      str === null ||
      str === 'null' ||
      str === undefined ||
      str === 'undefined'
    ) {
      return '-';
    }
    return str;
  }
  // 处理重合项目点击事件的全局函数
  const handleOverlapItemClick = (
    userName,
    userType,
    coords,
    phone,
    userId,
    city,
    ip,
    userBeanStr,
  ) => {
    console.log(
      `点击了重合位置的${userType === 'coach' ? '教练' : '用户'}:`,
      userName,
    );
    console.log('userBeanStr', userBeanStr);
    const userBean = JSON.parse(userBeanStr);

    // 关闭当前弹窗
    mapRef.current.closePopup();

    // 创建单独的信息弹窗
    const detailPopup = L.popup({
      maxWidth: 400,
      className: 'user-detail-popup',
      closeOnClick: true,
      autoClose: false,
      closeOnEscapeKey: true,
    })
      .setLatLng(coords)
      .setContent(
        `
      <div style="padding: 10px;">
        <h3 style="margin: 0 0 10px 0; color: ${
          userType === 'coach' ? '#ff6b6b' : '#46bee9'
        };">${userName}</h3>
        <div style="margin-bottom: 8px;">
          <strong>类型:</strong> ${userType === 'coach' ? '教练' : '用户'}
        </div>
        <div style="margin-bottom: 8px;">
          <strong>IP:</strong> ${getNullString(ip)}
        </div>
        <div style="margin-bottom: 12px;">
        ${
          userType === 'user'
            ? `<div style="margin-bottom: 8px;">
            <strong>城市:</strong> ${getNullString(city)}
          </div>
          <div style="margin-bottom: 8px;">
            <strong>总完课数:</strong> ${getNullString(
              userBean?.data?.finishClassCount,
            )}次
          </div>
          <div style="margin-bottom: 8px;">
            <strong>总训练分钟数:</strong> ${getNullString(
              userBean?.data?.totalMinutes,
            )}分钟
          </div>
          <div style="margin-bottom: 8px;">
            <strong>最近30天运动打卡次数:</strong> ${getNullString(
              userBean?.data?.finishCountLatest30,
            )}次
          </div>
          <div style="margin-bottom: 8px;">
            <strong>最近3个月消费金额:</strong> ${getNullString(
              userBean?.data?.orderCostLatest90,
            )}元
          </div>
          <div style="margin-bottom: 8px;">
            <strong>最近活跃间隔:</strong> ${getNullString(
              userBean?.data?.recencyInteractionDays,
            )}天
          </div>
          <div style="margin-bottom: 8px;">
            <strong>用户分群:</strong> ${getNullString(userBean?.data?.rfmType)}
          </div>`
            : ''
        }

        </div>
        <div style="display: flex; justify-content: center;">
          <button onclick="window.handleUserAction('${userName}', 'view', '${userId}', '${phone}')"
                  style="
                    padding: 6px 12px;
                    background: ${userType === 'coach' ? '#ff6b6b' : '#46bee9'};
                    color: white;
                    border: none;
                    border-radius: 4px;
                    cursor: pointer;
                    font-size: 12px;
                  ">查看详情</button>
        </div>
      </div>
    `,
      )
      .openOn(mapRef.current);
  };

  // 处理用户操作的全局函数
  const handleUserAction = (userName, action, userId, phone) => {
    console.log(`对用户 ${userName} 执行操作:`, action);

    switch (action) {
      case 'view':
        // 查看用户详情
        toUserPage(userId, phone);

        break;

      case 'contact':
        // 联系用户
        alert(`联系 ${userName}`);
        // 这里可以打开聊天窗口或显示联系方式
        break;

      default:
        console.log('未知操作:', action);
    }

    // 关闭弹窗
    mapRef.current.closePopup();
  };

  // 将函数挂载到全局window对象，以便HTML中的onclick可以访问
  useEffect(() => {
    window.handleOverlapItemClick = handleOverlapItemClick;
    window.handleUserAction = handleUserAction;

    // 清理函数
    return () => {
      delete window.handleOverlapItemClick;
      delete window.handleUserAction;
    };
  }, []);

  // 城市点击处理函数
  const handleCityClick = (params) => {
    console.log('点击了城市:', params.name);
    console.log('城市坐标:', params.coords);
    console.log('params:', params);

    // 查找重合的用户
    const overlappingItems = findOverlappingUsers(params.coords);

    // 创建一个临时的高亮标记
    const highlightMarker = L.marker(params.coords, {
      icon: L.divIcon({
        className: 'overlap-highlight-marker',
        html: `<div style="
          width: 40px;
          height: 40px;
          background-color: ${params.type === 'coach' ? '#ff6b6b' : '#46bee9'};
          border: 3px solid #ffffff;
          border-radius: 50%;
          animation: pulse-highlight 2s infinite;
          box-shadow: 0 0 10px rgba(${
            params.type === 'coach' ? '255, 68, 68' : '70, 189, 228'
          }, 0.6);
        "></div>`,
        iconSize: [40, 40],
        iconAnchor: [20, 20],
      }),
    });

    // 添加高亮标记到地图
    highlightMarker.addTo(mapRef.current);

    if (overlappingItems.length > 1) {
      console.log('发现重合坐标:', overlappingItems);
      // 显示重合信息的弹窗
      const detailPopup = L.popup({
        maxWidth: 250,
        className: 'overlap-popup',
        closeOnClick: true,
        autoClose: false,
        closeOnEscapeKey: true,
      })
        .setLatLng(params.coords)
        .setContent(createOverlapPopupContent(overlappingItems))
        .openOn(mapRef.current);
    } else {
      // 即使没有重合，也显示单个用户的详细信息弹窗
      const detailPopup = L.popup({
        maxWidth: 400,
        className: 'user-detail-popup',
        closeOnClick: true,
        autoClose: false,
        closeOnEscapeKey: true,
      })
        .setLatLng(params.coords)
        .setContent(
          `
        <div style="padding: 10px; overflow: hidden;">
          <h3 style="margin: 0 0 10px 0; color: ${
            params.type === 'coach' ? '#ff6b6b' : '#46bee9'
          };">${params.name}</h3>
          <div style="margin-bottom: 8px;">
            <strong>类型:</strong> ${params.type === 'coach' ? '教练' : '用户'}
          </div>
          <div style="margin-bottom: 8px;">
            <strong>IP:</strong> ${getNullString(params?.data?.ip)}
          </div>
          <div style="margin-bottom: 12px;">
          ${
            params.type === 'user'
              ? `<div style="margin-bottom: 8px;">
            <strong>城市:</strong> ${getNullString(params?.data?.city)}
          </div>
          <div style="margin-bottom: 8px;">
            <strong>总完课数:</strong> ${getNullString(
              params?.data?.finishClassCount,
            )}
          </div>
          <div style="margin-bottom: 8px;">
            <strong>总训练分钟数:</strong> ${getNullString(
              params?.data?.totalMinutes,
            )}分钟
          </div>
          <div style="margin-bottom: 8px;">
            <strong>最近30天运动打卡次数:</strong> ${getNullString(
              params?.data?.finishCountLatest30,
            )}次
          </div>
          <div style="margin-bottom: 8px;">
            <strong>最近3个月消费金额:</strong> ${getNullString(
              params?.data?.orderCostLatest90,
            )}元
          </div>
          <div style="margin-bottom: 8px;">
            <strong>最近活跃间隔:</strong> ${getNullString(
              params?.data?.recencyInteractionDays,
            )}天
          </div>
          <div style="margin-bottom: 8px;">
            <strong>用户分群:</strong> ${getNullString(params?.data?.rfmType)}
          </div>`
              : ''
          }
          </div>
          <div style="display: flex; justify-content: center;">
            <button onclick="window.handleUserAction('${
              params.name
            }', 'view', '${
            params.type === 'coach'
              ? params?.data?.coachUserId
              : params?.data?.userId
          }', '${
            params.type === 'coach'
              ? params?.data?.coachPhone
              : params?.data?.phone
          }')"
                    style="
                      padding: 6px 12px;
                      background: ${
                        params.type === 'coach' ? '#ff6b6b' : '#46bee9'
                      };
                      color: white;
                      border: none;
                      border-radius: 4px;
                      cursor: pointer;
                      font-size: 12px;
                    ">查看详情</button>
          </div>
        </div>
      `,
        )
        .openOn(mapRef.current);
    }

    // 3秒后移除高亮标记
    setTimeout(() => {
      if (mapRef.current && mapRef.current.hasLayer(highlightMarker)) {
        mapRef.current.removeLayer(highlightMarker);
      }
    }, 3000);

    // 在这里添加你的其他点击处理逻辑
  };

  // 流向线点击处理函数
  const handleFlowClick = (flow) => {
    console.log('点击了流向线:', flow.fromName + ' → ' + flow.toName);
    console.log('流量值:', flow.value);

    // 在这里添加你的流向线点击处理逻辑
  };

  function toUserPage(userId, phone) {
    window.open(`/user/record?id=${userId}&phone=${phone}`, '_blank');
  }
  // 修改生成热力图数据的函数，增强热力效果
  const generateHeatmapData = () => {
    const heatData = [];
    const regionCount = {}; // 统计每个区域的用户数量

    // 获取教练坐标用于转换
    const coachLat = coachData?.coachLatitude
      ? parseFloat(coachData.coachLatitude)
      : null;
    const coachLng = coachData?.coachLongitude
      ? parseFloat(coachData.coachLongitude)
      : null;

    // 使用过滤后的用户数据
    const currentUsers = getCurrentUserData();

    // 统计每个区域的用户数量
    currentUsers?.forEach((member) => {
      if (!member?.latitude || !member?.longitude) return;

      const originalLat = parseFloat(member.latitude);
      const originalLng = parseFloat(member.longitude);

      let lat, lng;

      // 如果教练坐标存在，使用转换后的坐标；否则使用原始坐标
      if (coachLat && coachLng) {
        const transformed = transformCoordinates(
          originalLat,
          originalLng,
          coachLat,
          coachLng,
        );
        lat = transformed.lat;
        lng = transformed.lng;
      } else {
        // 教练坐标为空时，使用用户原始坐标
        lat = originalLat;
        lng = originalLng;
      }

      const key = `${lat.toFixed(2)}_${lng.toFixed(2)}`; // 精度到小数点后2位进行区域聚合

      if (!regionCount[key]) {
        regionCount[key] = {
          lat: lat,
          lng: lng,
          count: 0,
        };
      }
      regionCount[key].count += 1;
    });

    // 转换为热力图数据格式 [lat, lng, intensity]
    Object.values(regionCount).forEach((region) => {
      // 增强强度计算，提高热力效果的明显程度
      const intensity = Math.min(region.count * 1.5, 2.0); // 增加权重系数从0.8到1.5，最大强度从1提高到2
      heatData.push([region.lat, region.lng, intensity]);
    });

    return heatData;
  };
  // 添加热力图层 - 增强视觉效果
  const addHeatmapLayer = () => {
    if (!mapRef.current) return;

    // 移除现有热力图层
    removeHeatmapLayer();

    const heatData = generateHeatmapData();

    if (heatData.length > 0) {
      heatLayerRef.current = L.heatLayer(heatData, {
        radius: 60, // 增加热力点半径从50到60
        blur: 25, // 减少模糊程度从35到25，让边界更清晰
        maxZoom: 19,
        max: 2.0, // 提高最大强度值从1.0到2.0
        minOpacity: 0.3, // 添加最小透明度，确保低强度区域也可见
        gradient: {
          // 调整颜色渐变，增强对比度
          0.0: 'rgba(0, 0, 255, 0.6)', // 蓝色，增加透明度
          0.15: 'rgba(0, 255, 255, 0.7)', // 青色
          0.3: 'rgba(0, 255, 0, 0.8)', // 绿色
          0.5: 'rgba(255, 255, 0, 0.9)', // 黄色
          0.7: 'rgba(255, 165, 0, 1.0)', // 橙色
          1.0: 'rgba(255, 0, 0, 1.0)', // 红色，完全不透明
        },
      }).addTo(mapRef.current);
    }
  };

  // 移除热力图层
  const removeHeatmapLayer = () => {
    if (heatLayerRef.current && mapRef.current) {
      mapRef.current.removeLayer(heatLayerRef.current);
      heatLayerRef.current = null;
    }
  };

  // 切换热力图显示
  const toggleHeatmap = () => {
    setShowHeatmap((prev) => {
      const newState = !prev;
      if (newState) {
        addHeatmapLayer();
      } else {
        removeHeatmapLayer();
      }
      return newState;
    });
  };

  // 修改初始化地图函数
  const initMap = () => {
    if (mapContainerRef.current && !mapRef.current) {
      // 创建地图实例
      mapRef.current = L.map(mapContainerRef.current, {
        center: [35.0, 105.0], // 中国中心坐标
        zoom: 4,
        minZoom: 2, // 添加最小缩放级别
        maxZoom: 19, // 添加最大缩放级别
        zoomControl: true,
        scrollWheelZoom: true,
        doubleClickZoom: true,
        boxZoom: true,
        keyboard: true,
        dragging: true,
        touchZoom: true,
        // attributionControl: false, // 隐藏右下角的 Leaflet 标志
      });
      // 添加地图图层
      L.tileLayer(
        'https://webrd0{s}.is.autonavi.com/appmaptile?lang=zh_cn&size=1&scale=1&style=7&x={x}&y={y}&z={z}',
        {
          attribution: '© 高德地图',
          maxZoom: 19,
          minZoom: 2, // 添加最小缩放级别
          subdomains: ['1', '2', '3', '4'],
        },
      ).addTo(mapRef.current);

      // 移除深色主题样式，改为亮色主题
      const style = document.createElement('style');
      style.textContent = `
        .custom-marker {
          // 移除反色滤镜
        }
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); }
        }
      `;
      document.head.appendChild(style);
      // 添加城市标记和流向线
      addCityMarkers();
      if (showFlowLines) {
        addFlowLines();
      }

      // 如果热力图开关是开启状态，添加热力图层
      if (showHeatmap) {
        addHeatmapLayer();
      }

      // 如果距离过滤开关是开启状态，添加范围圆圈
      if (showDistanceFilter) {
        toggleRangeCircle(true);
      }
    }
  };

  // 组件挂载时初始化
  useEffect(() => {
    // 只有当coachData有数据时才初始化地图和添加标记
    if (coachData && Object.keys(coachData).length > 0) {
      initMap();
    }

    // 清理函数
    return () => {
      if (mapRef.current) {
        removeHeatmapLayer(); // 清理热力图层
        if (rangeCircleRef.current) {
          mapRef.current.removeLayer(rangeCircleRef.current);
        }
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [coachData]); // 添加coachData作为依赖项

  // 监听过滤状态变化，更新地图显示
  useEffect(() => {
    if (mapRef.current && coachData && Object.keys(coachData).length > 0) {
      // 重新添加标记和流向线
      addCityMarkers();
      if (showFlowLines) {
        addFlowLines();
      }

      // 如果热力图是开启状态，重新生成热力图
      if (showHeatmap) {
        addHeatmapLayer();
      }
    }
  }, [showDistanceFilter, filterDistance, showRightsFilter, rfmFilter]); // 添加showRightsFilter和rfmFilter到依赖项

  return (
    <div className="flow-map-container">
      {/* 添加控制面板 */}
      <div className="map-controls">
        <button
          className={`heatmap-toggle ${showHeatmap ? 'active' : ''}`}
          onClick={toggleHeatmap}
          title="切换热力图显示"
        >
          <span className="toggle-icon">{showHeatmap ? '🔥' : '📊'}</span>
          <span className="toggle-text">
            {showHeatmap ? '关闭热力图' : '开启热力图'}
          </span>
        </button>

        <div className="distance-filter-container">
          <button
            className={`distance-filter-toggle ${
              showDistanceFilter ? 'active' : ''
            }`}
            onClick={toggleDistanceFilter}
            title="切换距离范围过滤"
          >
            <span className="toggle-icon">
              {showDistanceFilter ? '🎯' : '🌐'}
            </span>
            <span className="toggle-text">
              {showDistanceFilter ? '关闭范围过滤' : '距离范围过滤'}
            </span>
          </button>

          {showDistanceFilter && (
            <div className="distance-input-container">
              <input
                type="number"
                min="10"
                max="1000"
                step="10"
                value={filterDistance}
                onChange={handleDistanceChange}
                className="distance-input"
                placeholder="距离(km)"
              />
              <span className="distance-unit">km</span>
            </div>
          )}
        </div>

        {/* 添加权益筛选按钮 */}
        <button
          className={`rights-filter-toggle ${showRightsFilter ? 'active' : ''}`}
          onClick={toggleRightsFilter}
          title="切换权益用户筛选"
        >
          <span className="toggle-icon">{showRightsFilter ? '👑' : '👥'}</span>
          <span className="toggle-text">
            {showRightsFilter ? '显示全部用户' : '仅显示权益用户'}
          </span>
        </button>

        {/* RFM用户分群筛选 */}
        <div className="rfm-filter-container">
          <label htmlFor="rfm-select" className="rfm-filter-label">
            用户分群:
          </label>
          <select
            id="rfm-select"
            value={rfmFilter}
            onChange={handleRfmFilterChange}
            className="rfm-filter-select"
          >
            {rfmOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <button
          className={`flow-lines-toggle ${showFlowLines ? 'active' : ''}`}
          onClick={toggleFlowLines}
          title="切换流向线显示"
        >
          <span className="toggle-icon">{showFlowLines ? '🔗' : '📍'}</span>
          <span className="toggle-text">
            {showFlowLines ? '关闭流向线' : '显示流向线'}
          </span>
        </button>

        {/* 用户统计信息 */}
        <div className="user-stats">
          <span className="stats-text">
            显示用户: {userStats.filtered}
            {(showDistanceFilter || showRightsFilter || rfmFilter !== 'all') &&
              userStats.total !== userStats.filtered && (
                <span className="total-users"> / {userStats.total}</span>
              )}
            {showRightsFilter && (
              <span className="rights-indicator"> (权益用户)</span>
            )}
            {rfmFilter !== 'all' && (
              <span className="rfm-indicator">
                {' '}
                ({rfmOptions.find((opt) => opt.value === rfmFilter)?.label})
              </span>
            )}
          </span>
        </div>

        {showHeatmap && (
          <div className="heatmap-legend">
            <span className="legend-title">用户密度:</span>
            <div className="legend-gradient">
              <span className="legend-label">低</span>
              <div className="gradient-bar"></div>
              <span className="legend-label">高</span>
            </div>
          </div>
        )}
      </div>

      <div
        ref={mapContainerRef}
        className="chart-container"
        style={{
          width: '100%',
          height: '100%',
          minHeight: '800px',
          backgroundColor: '#bbd5e8',
        }}
      />
    </div>
  );
};

export default HotChart;
